import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigation } from './Navigation';
import {
  lifecycleService,
  SimulationResult,
  UserProfile,
  Assumptions,
  AllocationWeights
} from '../services/lifecycleService';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip,
  ReferenceArea, Legend, ResponsiveContainer
} from 'recharts';
import './Dashboard.css';
import './LifecycleInvestment.css';

/** Lightweight markdown-to-HTML for AI chat responses. */
const formatMarkdown = (text: string): string => {
  const lines = text.split('\n');
  const output: string[] = [];
  let inList = false;

  for (const line of lines) {
    const bulletMatch = line.match(/^- (.+)$/);
    if (bulletMatch) {
      if (!inList) { output.push('<ul>'); inList = true; }
      output.push(`<li>${bulletMatch[1]}</li>`);
    } else {
      if (inList) { output.push('</ul>'); inList = false; }
      output.push(line === '' ? '<br/>' : line + '<br/>');
    }
  }
  if (inList) output.push('</ul>');

  return output.join('')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
};

type TabType = 'input' | 'results' | 'comparison' | 'projections' | 'chat';

// Default values
const DEFAULT_PROFILE: UserProfile = {
  current_age: 30,
  retirement_age: 65,
  life_expectancy: 95,
  current_savings: 50000,
  annual_income: 100000,
  annual_expenses: 60000,
  emergency_fund_months: 6,
  filing_status: 'single',
  custom_tax_rate: null,
  ss_monthly_benefit: 0,
  ss_start_age: 67,
  roth_conversion_enabled: false,
  pre_tax_percentage: 1.0,
  roth_target_bracket: 0.22,
  rmd_start_age: 73
};

const DEFAULT_ASSUMPTIONS: Assumptions = {
  inflation_rate: 0.025,
  savings_return: 0.01,
  growth_return: 0.07,
  dividend_return: 0.04,
  super_long_term_return: 0.09,
  contribution_growth_rate: 0.03
};

const DEFAULT_ALLOCATION: AllocationWeights = {
  savings: 0.10,
  growth: 0.40,
  dividend: 0.20,
  super_long_term: 0.30
};

export const LifecycleInvestment: React.FC = () => {
  const { token, user } = useAuth();
  const isPremier = user?.effectiveTier === 'premier' || user?.effectiveTier === 'pro';

  // Form state
  const [profile, setProfile] = useState<UserProfile>(DEFAULT_PROFILE);
  const [assumptions, setAssumptions] = useState<Assumptions>(DEFAULT_ASSUMPTIONS);
  const [allocation, setAllocation] = useState<AllocationWeights>(DEFAULT_ALLOCATION);
  const [annualContribution, setAnnualContribution] = useState<number>(20000);
  const [stressMode, setStressMode] = useState<'none' | 'fast' | 'slow' | 'both'>('both');
  const [stressDrawdownPct, setStressDrawdownPct] = useState<number>(0.30);
  const [stressTiming, setStressTiming] = useState<'pre_retirement' | 'early_retirement' | 'mid_retirement'>('pre_retirement');

  // UI state
  const [activeTab, setActiveTab] = useState<TabType>('input');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showGuide, setShowGuide] = useState(false);

  // Results state
  const [result, setResult] = useState<SimulationResult | null>(null);

  // Chat state
  const [chatQuestion, setChatQuestion] = useState('');
  const [chatHistory, setChatHistory] = useState<Array<{ question: string; answer: string }>>([]);
  const [chatLoading, setChatLoading] = useState(false);

  // Comparison state
  const [comparisonStrategy, setComparisonStrategy] = useState<'sixty_forty' | 'age_in_bonds'>('sixty_forty');

  // Handlers
  const handleProfileChange = (field: keyof UserProfile, value: any) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleAssumptionChange = (field: keyof Assumptions, value: number) => {
    setAssumptions(prev => ({ ...prev, [field]: value }));
  };

  const handleAllocationChange = (field: keyof AllocationWeights, value: number) => {
    // Normalize to ensure sum is 1.0
    const newAllocation = { ...allocation, [field]: value };
    setAllocation(newAllocation);
  };

  const normalizeAllocation = () => {
    const total = allocation.savings + allocation.growth + allocation.dividend + allocation.super_long_term;
    if (Math.abs(total - 1.0) > 0.001) {
      const factor = 1.0 / total;
      setAllocation({
        savings: Math.round(allocation.savings * factor * 100) / 100,
        growth: Math.round(allocation.growth * factor * 100) / 100,
        dividend: Math.round(allocation.dividend * factor * 100) / 100,
        super_long_term: Math.round(allocation.super_long_term * factor * 100) / 100
      });
    }
  };

  const runSimulation = async () => {
    if (!token) {
      setError('Authentication required');
      return;
    }

    // Validate allocation sums to 1
    const allocationSum = allocation.savings + allocation.growth + allocation.dividend + allocation.super_long_term;
    if (Math.abs(allocationSum - 1.0) > 0.01) {
      setError(`Allocation must sum to 100%. Current sum: ${(allocationSum * 100).toFixed(1)}%`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const simResult = await lifecycleService.runSimulation(token, {
        profile,
        assumptions,
        allocation,
        annual_contribution: annualContribution,
        stress_mode: stressMode,
        stress_drawdown_pct: stressDrawdownPct,
        stress_timing: stressTiming
      });

      setResult(simResult);
      setActiveTab('results');
      setChatHistory([]);
    } catch (err: any) {
      console.error('Simulation error:', err);
      setError(err.response?.data?.message || 'Failed to run simulation');
    } finally {
      setLoading(false);
    }
  };

  const sendChatMessage = async () => {
    if (!token || !result || !chatQuestion.trim() || !isPremier) return;

    setChatLoading(true);
    try {
      const response = await lifecycleService.chat(token, result.run_id, chatQuestion, {
        kpis: result.kpis,
        inputs: result.inputs,
        stress_results: result.stress_results,
        chat_history: chatHistory.slice(-5),
      });
      setChatHistory(prev => [...prev, { question: chatQuestion, answer: response.answer }]);
      setChatQuestion('');
    } catch (err: any) {
      console.error('Chat error:', err);
      setError(err.response?.data?.message || 'Failed to send message');
    } finally {
      setChatLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const generatePDF = () => {
    if (!result) return;

    const doc = new jsPDF();
    const { kpis } = result;
    let yPos = 20;

    // Title
    doc.setFontSize(20);
    doc.setTextColor(26, 26, 46);
    doc.text('Lifecycle Investment Plan Report', 105, yPos, { align: 'center' });
    yPos += 10;

    // Date
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 105, yPos, { align: 'center' });
    yPos += 15;

    // Summary Section
    doc.setFontSize(14);
    doc.setTextColor(26, 115, 232);
    doc.text('Plan Summary', 14, yPos);
    yPos += 8;

    doc.setFontSize(11);
    doc.setTextColor(50);
    const summaryText = `Based on your inputs, you'll contribute for ${kpis.years_to_retirement} years until age ${profile.retirement_age}, ` +
      `building a portfolio of ${formatCurrency(kpis.projected_retirement_balance)}. In retirement, you can withdraw ` +
      `${formatCurrency(kpis.annual_retirement_income)}/year, replacing ${formatPercent(kpis.income_replacement_ratio)} of your ` +
      `pre-retirement income. Your portfolio is projected to last ${kpis.years_of_income} years, leaving ` +
      `${formatCurrency(kpis.legacy_amount)} at age ${profile.life_expectancy}.`;

    const splitSummary = doc.splitTextToSize(summaryText, 180);
    doc.text(splitSummary, 14, yPos);
    yPos += splitSummary.length * 6 + 10;

    // Input Parameters Section
    doc.setFontSize(14);
    doc.setTextColor(26, 115, 232);
    doc.text('Input Parameters', 14, yPos);
    yPos += 5;

    autoTable(doc, {
      startY: yPos,
      head: [['Parameter', 'Value']],
      body: [
        ['Current Age', profile.current_age.toString()],
        ['Retirement Age', profile.retirement_age.toString()],
        ['Life Expectancy', profile.life_expectancy?.toString() || '95'],
        ['Current Savings', formatCurrency(profile.current_savings)],
        ['Annual Net Income', formatCurrency(profile.annual_income)],
        ['Annual Expenses', formatCurrency(profile.annual_expenses)],
        ['Annual Contribution', formatCurrency(annualContribution)],
        ['Savings Allocation', formatPercent(allocation.savings)],
        ['Growth Allocation', formatPercent(allocation.growth)],
        ['Dividend Allocation', formatPercent(allocation.dividend)],
        ['Super Long Term Allocation', formatPercent(allocation.super_long_term)],
        ['Inflation Rate', formatPercent(assumptions.inflation_rate || 0.025)],
        ['Filing Status', profile.filing_status === 'married_joint' ? 'Married Filing Jointly' : 'Single'],
        ['Custom Tax Rate', profile.custom_tax_rate != null ? formatPercent(profile.custom_tax_rate) : 'Federal Brackets'],
        ['SS Monthly Benefit', formatCurrency(profile.ss_monthly_benefit || 0)],
        ['SS Start Age', (profile.ss_start_age || 67).toString()],
        ...(profile.roth_conversion_enabled ? [
          ['Roth Conversion', 'Enabled'],
          ['Pre-Tax Portfolio', formatPercent(profile.pre_tax_percentage ?? 1.0)],
          ['Target Bracket', formatPercent(profile.roth_target_bracket ?? 0.22)],
          ['RMD Start Age', (profile.rmd_start_age ?? 73).toString()],
        ] : []),
        ['Stress Test Drawdown', formatPercent(stressDrawdownPct)],
        ['Stress Test Timing', stressTiming.replace('_', ' ')],
      ],
      theme: 'striped',
      headStyles: { fillColor: [26, 115, 232] },
      margin: { left: 14, right: 14 },
    });

    yPos = (doc as any).lastAutoTable.finalY + 15;

    // Key Performance Indicators
    doc.setFontSize(14);
    doc.setTextColor(26, 115, 232);
    doc.text('Key Performance Indicators', 14, yPos);
    yPos += 5;

    autoTable(doc, {
      startY: yPos,
      head: [['Metric', 'Value']],
      body: [
        ['Years to Retirement', kpis.years_to_retirement.toString()],
        ['Total Contributions', formatCurrency(kpis.total_contributions)],
        ['Projected Retirement Balance', formatCurrency(kpis.projected_retirement_balance)],
        ['Annual Retirement Income', formatCurrency(kpis.annual_retirement_income)],
        ['Income Replacement Ratio', formatPercent(kpis.income_replacement_ratio)],
        ['Years of Income', kpis.years_of_income.toString()],
        ['Legacy Amount', formatCurrency(kpis.legacy_amount)],
        ['Net Retirement Income', formatCurrency(kpis.annual_net_retirement_income)],
        ['Effective Tax Rate', formatPercent(kpis.effective_tax_rate)],
        ['Total SS Income', formatCurrency(kpis.total_ss_income)],
        ...(kpis.total_roth_conversions > 0 ? [
          ['Total Roth Conversions', formatCurrency(kpis.total_roth_conversions)],
          ['Conversion Tax Cost', formatCurrency(kpis.roth_conversion_tax_cost)],
        ] : []),
        ['Transition Start Age', kpis.transition_start_age.toString()],
        ['Glide Path Years', kpis.glide_path_years.toString()],
      ],
      theme: 'striped',
      headStyles: { fillColor: [26, 115, 232] },
      margin: { left: 14, right: 14 },
    });

    yPos = (doc as any).lastAutoTable.finalY + 15;

    // Stress Test Results
    if (result.stress_results.length > 0) {
      doc.setFontSize(14);
      doc.setTextColor(26, 115, 232);
      doc.text('Stress Test Results', 14, yPos);
      yPos += 5;

      autoTable(doc, {
        startY: yPos,
        head: [['Scenario', 'Drawdown', 'Recovery', 'Pre-Stress', 'Post-Stress', 'Status']],
        body: result.stress_results.map(s => [
          s.name,
          formatPercent(s.drawdown_pct),
          `${s.recovery_years} years`,
          formatCurrency(s.pre_stress_balance),
          formatCurrency(s.post_stress_balance),
          s.survived ? 'SURVIVED' : 'FAILED'
        ]),
        theme: 'striped',
        headStyles: { fillColor: [26, 115, 232] },
        margin: { left: 14, right: 14 },
        didParseCell: (data) => {
          if (data.column.index === 5 && data.section === 'body') {
            data.cell.styles.fontStyle = 'bold';
            if (data.cell.raw === 'SURVIVED') {
              data.cell.styles.textColor = [34, 197, 94];
            } else {
              data.cell.styles.textColor = [239, 68, 68];
            }
          }
        },
      });

      yPos = (doc as any).lastAutoTable.finalY + 15;
    }

    // New page for projections
    doc.addPage();
    yPos = 20;

    // Yearly Projections
    doc.setFontSize(14);
    doc.setTextColor(26, 115, 232);
    doc.text('Yearly Projections', 14, yPos);
    yPos += 3;

    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text('Values shown are for each specific year, not cumulative totals.', 14, yPos + 5);
    yPos += 10;

    // Filter to show milestone years (every 5 years for PDF)
    const projections = result.yearly_projections.filter(
      (p, idx) => idx === 0 || p.age % 5 === 0 || idx === result.yearly_projections.length - 1
    );

    autoTable(doc, {
      startY: yPos,
      head: [['Age', 'Phase', 'Balance', 'Contrib.', 'Withdrawal', 'Growth', 'Tax', 'Net', 'SS', ...(profile.roth_conversion_enabled ? ['Roth'] : [])]],
      body: projections.map(p => [
        p.age.toString(),
        p.phase.charAt(0).toUpperCase() + p.phase.slice(1),
        formatCurrency(p.total_balance),
        formatCurrency(p.total_contribution),
        formatCurrency(p.total_withdrawal),
        formatCurrency(p.total_growth),
        formatCurrency(p.tax_amount || 0),
        formatCurrency(p.net_withdrawal || 0),
        formatCurrency(p.ss_income || 0),
        ...(profile.roth_conversion_enabled ? [formatCurrency(p.roth_conversion || 0)] : [])
      ]),
      theme: 'striped',
      headStyles: { fillColor: [26, 115, 232] },
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9 },
    });

    // Footer
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`Page ${i} of ${pageCount}`, 105, 290, { align: 'center' });
      doc.text('Generated by IV Monitor - Lifecycle Investment Planner', 105, 295, { align: 'center' });
    }

    // Save
    doc.save(`lifecycle-plan-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // Render helpers
  const renderInputTab = () => (
    <div className="lifecycle-form">
      {/* Profile Section */}
      <div className="form-section">
        <h3>Your Profile</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Current Age</label>
            <input
              type="number"
              value={profile.current_age}
              onChange={e => handleProfileChange('current_age', parseInt(e.target.value) || 0)}
              min={18}
              max={100}
            />
          </div>
          <div className="form-group">
            <label>Retirement Age</label>
            <input
              type="number"
              value={profile.retirement_age}
              onChange={e => handleProfileChange('retirement_age', parseInt(e.target.value) || 0)}
              min={40}
              max={100}
            />
          </div>
          <div className="form-group">
            <label>Life Expectancy</label>
            <input
              type="number"
              value={profile.life_expectancy}
              onChange={e => handleProfileChange('life_expectancy', parseInt(e.target.value) || 0)}
              min={70}
              max={120}
            />
          </div>
          <div className="form-group">
            <label>Current Savings ($)</label>
            <input
              type="number"
              value={profile.current_savings}
              onChange={e => handleProfileChange('current_savings', parseFloat(e.target.value) || 0)}
              min={0}
              step={1000}
            />
          </div>
          <div className="form-group">
            <label>Annual Net Income ($)</label>
            <input
              type="number"
              value={profile.annual_income}
              onChange={e => handleProfileChange('annual_income', parseFloat(e.target.value) || 0)}
              min={0}
              step={1000}
            />
          </div>
          <div className="form-group">
            <label>Annual Expenses ($)</label>
            <input
              type="number"
              value={profile.annual_expenses}
              onChange={e => handleProfileChange('annual_expenses', parseFloat(e.target.value) || 0)}
              min={0}
              step={1000}
            />
          </div>
          <div className="form-group">
            <label>Annual Contribution ($)</label>
            <input
              type="number"
              value={annualContribution}
              onChange={e => setAnnualContribution(parseFloat(e.target.value) || 0)}
              min={0}
              step={1000}
            />
          </div>
          <div className="form-group">
            <label>Inflation Rate ({formatPercent(assumptions.inflation_rate || 0.025)})</label>
            <input
              type="range"
              value={(assumptions.inflation_rate || 0.025) * 100}
              onChange={e => handleAssumptionChange('inflation_rate', parseFloat(e.target.value) / 100)}
              min={0}
              max={10}
              step={0.5}
            />
          </div>
        </div>
      </div>

      {/* Social Security & Taxes Section */}
      <div className="form-section">
        <h3>Social Security & Taxes</h3>
        <p className="form-hint">Configure Social Security benefits and tax treatment for retirement withdrawals</p>
        <div className="form-grid">
          <div className="form-group">
            <label>SS Monthly Benefit ($)</label>
            <input
              type="number"
              value={profile.ss_monthly_benefit || 0}
              onChange={e => handleProfileChange('ss_monthly_benefit', parseFloat(e.target.value) || 0)}
              min={0}
              max={10000}
              step={100}
            />
          </div>
          <div className="form-group">
            <label>SS Start Age</label>
            <input
              type="number"
              value={profile.ss_start_age || 67}
              onChange={e => handleProfileChange('ss_start_age', parseInt(e.target.value) || 67)}
              min={62}
              max={70}
            />
          </div>
          <div className="form-group">
            <label>Filing Status</label>
            <select
              value={profile.filing_status || 'single'}
              onChange={e => handleProfileChange('filing_status', e.target.value)}
            >
              <option value="single">Single</option>
              <option value="married_joint">Married Filing Jointly</option>
            </select>
          </div>
          <div className="form-group">
            <label>Custom Tax Rate (%)</label>
            <input
              type="number"
              value={profile.custom_tax_rate != null ? profile.custom_tax_rate * 100 : ''}
              onChange={e => {
                const val = e.target.value;
                handleProfileChange('custom_tax_rate', val === '' ? null : parseFloat(val) / 100);
              }}
              min={0}
              max={50}
              step={0.5}
              placeholder="Leave blank for brackets"
            />
            <span className="form-hint" style={{ marginTop: 4 }}>
              {profile.custom_tax_rate != null
                ? `Using ${formatPercent(profile.custom_tax_rate)} flat rate`
                : 'Using 2025 federal brackets'}
            </span>
          </div>
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={profile.roth_conversion_enabled || false}
                onChange={e => handleProfileChange('roth_conversion_enabled', e.target.checked)}
              />
              Enable Roth Conversion Strategy (Goldilocks Period)
            </label>
            <span className="form-hint" style={{ marginTop: 4 }}>
              Convert pre-tax money to Roth during the low-income window between retirement and SS/RMDs
            </span>
          </div>
          {profile.roth_conversion_enabled && (
            <>
              <div className="form-group">
                <label>Pre-Tax Portfolio ({Math.round((profile.pre_tax_percentage ?? 1.0) * 100)}%)</label>
                <input
                  type="range"
                  value={(profile.pre_tax_percentage ?? 1.0) * 100}
                  onChange={e => handleProfileChange('pre_tax_percentage', parseFloat(e.target.value) / 100)}
                  min={0}
                  max={100}
                  step={5}
                />
              </div>
              <div className="form-group">
                <label>Target Tax Bracket</label>
                <select
                  value={profile.roth_target_bracket ?? 0.22}
                  onChange={e => handleProfileChange('roth_target_bracket', parseFloat(e.target.value))}
                >
                  <option value={0.10}>10%</option>
                  <option value={0.12}>12%</option>
                  <option value={0.22}>22%</option>
                  <option value={0.24}>24%</option>
                  <option value={0.32}>32%</option>
                  <option value={0.35}>35%</option>
                </select>
              </div>
              <div className="form-group">
                <label>RMD Start Age</label>
                <input
                  type="number"
                  value={profile.rmd_start_age ?? 73}
                  onChange={e => handleProfileChange('rmd_start_age', parseInt(e.target.value) || 73)}
                  min={70}
                  max={75}
                />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <span className="form-hint">
                  Goldilocks window: age {profile.retirement_age} to {Math.min(profile.ss_start_age || 67, profile.rmd_start_age ?? 73) - 1}
                  {' '}({Math.max(0, Math.min(profile.ss_start_age || 67, profile.rmd_start_age ?? 73) - profile.retirement_age)} years)
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Allocation Section */}
      <div className="form-section">
        <h3>Bucket Allocation</h3>
        <p className="form-hint">Allocate your contributions across investment buckets (must total 100%)</p>
        <div className="form-grid">
          <div className="form-group">
            <label>Savings ({formatPercent(allocation.savings)})</label>
            <input
              type="range"
              value={allocation.savings * 100}
              onChange={e => handleAllocationChange('savings', parseFloat(e.target.value) / 100)}
              min={0}
              max={100}
              step={5}
            />
          </div>
          <div className="form-group">
            <label>Growth ({formatPercent(allocation.growth)})</label>
            <input
              type="range"
              value={allocation.growth * 100}
              onChange={e => handleAllocationChange('growth', parseFloat(e.target.value) / 100)}
              min={0}
              max={100}
              step={5}
            />
          </div>
          <div className="form-group">
            <label>Dividend ({formatPercent(allocation.dividend)})</label>
            <input
              type="range"
              value={allocation.dividend * 100}
              onChange={e => handleAllocationChange('dividend', parseFloat(e.target.value) / 100)}
              min={0}
              max={100}
              step={5}
            />
          </div>
          <div className="form-group">
            <label>Super Long Term ({formatPercent(allocation.super_long_term)})</label>
            <input
              type="range"
              value={allocation.super_long_term * 100}
              onChange={e => handleAllocationChange('super_long_term', parseFloat(e.target.value) / 100)}
              min={0}
              max={100}
              step={5}
            />
          </div>
        </div>
        <div className="allocation-total">
          Total: {formatPercent(allocation.savings + allocation.growth + allocation.dividend + allocation.super_long_term)}
          {Math.abs((allocation.savings + allocation.growth + allocation.dividend + allocation.super_long_term) - 1.0) > 0.01 && (
            <button className="btn-normalize" onClick={normalizeAllocation}>Normalize to 100%</button>
          )}
        </div>
      </div>

      {/* Stress Test Options */}
      <div className="form-section">
        <h3>Stress Testing</h3>
        <p className="form-hint">Simulate market crashes to test portfolio resilience</p>
        <div className="form-grid">
          <div className="form-group">
            <label>Stress Test Mode</label>
            <select value={stressMode} onChange={e => setStressMode(e.target.value as any)}>
              <option value="both">Both Scenarios</option>
              <option value="fast">Fast Recovery Only (1 year)</option>
              <option value="slow">Slow Recovery Only (5 years)</option>
              <option value="none">No Stress Test</option>
            </select>
          </div>
          <div className="form-group">
            <label>Market Drawdown ({formatPercent(stressDrawdownPct)})</label>
            <input
              type="range"
              value={stressDrawdownPct * 100}
              onChange={e => setStressDrawdownPct(parseFloat(e.target.value) / 100)}
              min={5}
              max={70}
              step={5}
              disabled={stressMode === 'none'}
            />
          </div>
          <div className="form-group">
            <label>Crash Timing</label>
            <select
              value={stressTiming}
              onChange={e => setStressTiming(e.target.value as any)}
              disabled={stressMode === 'none'}
            >
              <option value="pre_retirement">2 Years Before Retirement</option>
              <option value="early_retirement">1 Year Into Retirement</option>
              <option value="mid_retirement">5 Years Into Retirement</option>
            </select>
          </div>
        </div>
      </div>

      {/* Run Button */}
      <div className="form-actions">
        <button
          className="btn-primary btn-run"
          onClick={runSimulation}
          disabled={loading}
        >
          {loading ? 'Running Simulation...' : 'Run Simulation'}
        </button>
      </div>
    </div>
  );

  const formatCompactCurrency = (value: number): string => {
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const renderPortfolioChart = () => {
    if (!result) return null;

    const data = result.yearly_projections.map(p => ({
      age: p.age,
      balance: p.total_balance,
      phase: p.phase
    }));

    // Find phase boundaries for ReferenceArea bands
    const phases = result.yearly_projections;
    const accumStart = phases.find(p => p.phase === 'accumulation')?.age;
    const accumEnd = [...phases].reverse().find(p => p.phase === 'accumulation')?.age;
    const transStart = phases.find(p => p.phase === 'transition')?.age;
    const transEnd = [...phases].reverse().find(p => p.phase === 'transition')?.age;
    const distStart = phases.find(p => p.phase === 'distribution')?.age;
    const distEnd = [...phases].reverse().find(p => p.phase === 'distribution')?.age;

    return (
      <div className="portfolio-chart-container">
        <h4>Portfolio Balance Over Time</h4>
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart data={data} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
            <defs>
              <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            {accumStart != null && accumEnd != null && (
              <ReferenceArea x1={accumStart} x2={accumEnd} fill="#3b82f6" fillOpacity={0.08} label={{ value: 'Accumulation', position: 'insideTop', fill: '#60a5fa', fontSize: 11 }} />
            )}
            {transStart != null && transEnd != null && (
              <ReferenceArea x1={transStart} x2={transEnd} fill="#f59e0b" fillOpacity={0.08} label={{ value: 'Transition', position: 'insideTop', fill: '#fbbf24', fontSize: 11 }} />
            )}
            {distStart != null && distEnd != null && (
              <ReferenceArea x1={distStart} x2={distEnd} fill="#22c55e" fillOpacity={0.08} label={{ value: 'Distribution', position: 'insideTop', fill: '#4ade80', fontSize: 11 }} />
            )}
            <XAxis dataKey="age" stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 12 }} />
            <YAxis stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 12 }} tickFormatter={formatCompactCurrency} />
            <Tooltip
              contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, color: '#e2e8f0' }}
              labelFormatter={(age) => `Age ${age}`}
              formatter={(value: any) => [formatCurrency(value ?? 0), 'Balance']}
            />
            <Area type="monotone" dataKey="balance" stroke="#3b82f6" strokeWidth={2} fill="url(#balanceGradient)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderResultsTab = () => {
    if (!result) return <p>No results yet. Run a simulation first.</p>;

    const { kpis } = result;

    return (
      <div className="lifecycle-results">
        {/* Portfolio Chart */}
        {renderPortfolioChart()}

        {/* Summary Explanation */}
        <div className="results-summary">
          <div className="summary-header">
            <h3>Your Retirement Plan Summary</h3>
            <button className="btn-download" onClick={generatePDF}>
              Download PDF Report
            </button>
          </div>
          <p>
            Based on your inputs, you'll contribute for <strong>{kpis.years_to_retirement} years</strong> until
            age {profile.retirement_age}, building a portfolio of <strong>{formatCurrency(kpis.projected_retirement_balance)}</strong>.
          </p>
          <p>
            In retirement, you can withdraw <strong>{formatCurrency(kpis.annual_retirement_income)}/year</strong> (adjusted
            for inflation), replacing <strong>{formatPercent(kpis.income_replacement_ratio)}</strong> of your
            pre-retirement income. Your portfolio is projected to last <strong>{kpis.years_of_income} years</strong>,
            leaving <strong>{formatCurrency(kpis.legacy_amount)}</strong> at age {profile.life_expectancy}.
          </p>
          {kpis.effective_tax_rate > 0 && (
            <p>
              Your estimated effective tax rate on retirement income is <strong>{formatPercent(kpis.effective_tax_rate)}</strong>,
              resulting in an average net income of <strong>{formatCurrency(kpis.annual_net_retirement_income)}/year</strong> after taxes.
            </p>
          )}
          {(profile.ss_monthly_benefit || 0) > 0 && (
            <p>
              Social Security benefits of <strong>{formatCurrency((profile.ss_monthly_benefit || 0) * 12)}/year</strong> begin
              at age {profile.ss_start_age || 67}, providing <strong>{formatCurrency(kpis.total_ss_income)}</strong> in
              lifetime benefits.
            </p>
          )}
          {profile.roth_conversion_enabled && kpis.total_roth_conversions > 0 && (
            <p>
              During the Goldilocks window (ages {profile.retirement_age}-{Math.min(profile.ss_start_age || 67, profile.rmd_start_age ?? 73) - 1}),
              you convert <strong>{formatCurrency(kpis.total_roth_conversions)}</strong> from pre-tax to Roth
              at a tax cost of <strong>{formatCurrency(kpis.roth_conversion_tax_cost)}</strong>, reducing future taxable withdrawals.
            </p>
          )}
        </div>

        <h3>Key Performance Indicators</h3>

        <div className="kpi-grid">
          <div className="kpi-card">
            <div className="kpi-label">Years to Retirement</div>
            <div className="kpi-value">{kpis.years_to_retirement}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Projected Retirement Balance</div>
            <div className="kpi-value">{formatCurrency(kpis.projected_retirement_balance)}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Total Contributions</div>
            <div className="kpi-value">{formatCurrency(kpis.total_contributions)}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Annual Retirement Income</div>
            <div className="kpi-value">{formatCurrency(kpis.annual_retirement_income)}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Net Retirement Income</div>
            <div className="kpi-value">{formatCurrency(kpis.annual_net_retirement_income)}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Effective Tax Rate</div>
            <div className="kpi-value">{formatPercent(kpis.effective_tax_rate)}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Income Replacement Ratio</div>
            <div className="kpi-value">{formatPercent(kpis.income_replacement_ratio)}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Years of Income</div>
            <div className="kpi-value">{kpis.years_of_income}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Legacy Amount</div>
            <div className="kpi-value">{formatCurrency(kpis.legacy_amount)}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Transition Start Age</div>
            <div className="kpi-value">{kpis.transition_start_age}</div>
          </div>
          {kpis.total_ss_income > 0 && (
            <div className="kpi-card">
              <div className="kpi-label">Total SS Income</div>
              <div className="kpi-value">{formatCurrency(kpis.total_ss_income)}</div>
            </div>
          )}
          {kpis.total_roth_conversions > 0 && (
            <div className="kpi-card">
              <div className="kpi-label">Total Roth Conversions</div>
              <div className="kpi-value">{formatCurrency(kpis.total_roth_conversions)}</div>
            </div>
          )}
          {kpis.roth_conversion_tax_cost > 0 && (
            <div className="kpi-card">
              <div className="kpi-label">Conversion Tax Cost</div>
              <div className="kpi-value">{formatCurrency(kpis.roth_conversion_tax_cost)}</div>
            </div>
          )}
        </div>

        {/* Stress Test Results */}
        {result.stress_results.length > 0 && (
          <>
            <h3>Stress Test Results</h3>
            <div className="stress-results">
              {result.stress_results.map((stress, idx) => (
                <div key={idx} className={`stress-card ${stress.survived ? 'survived' : 'failed'}`}>
                  <div className="stress-header">
                    <span className="stress-name">{stress.name}</span>
                    <span className={`stress-status ${stress.survived ? 'survived' : 'failed'}`}>
                      {stress.survived ? 'SURVIVED' : 'FAILED'}
                    </span>
                  </div>
                  <div className="stress-details">
                    <div>Drawdown: {formatPercent(stress.drawdown_pct)}</div>
                    <div>Recovery Years: {stress.recovery_years}</div>
                    <div>Pre-Stress: {formatCurrency(stress.pre_stress_balance)}</div>
                    <div>Post-Stress: {formatCurrency(stress.post_stress_balance)}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderProjectionsTab = () => {
    if (!result) return <p>No results yet. Run a simulation first.</p>;

    // Show key milestone years
    const milestones = result.yearly_projections.filter(
      (p, idx) => idx === 0 || p.age % 10 === 0 || p.phase === 'transition' || idx === result.yearly_projections.length - 1
    );

    return (
      <div className="lifecycle-projections">
        <h3>Yearly Projections</h3>
        <p className="projections-hint">
          Each row shows a snapshot at year-end. Contribution, Withdrawal, and Growth are amounts for that specific year, not cumulative totals.
        </p>
        <div className="projections-table-container">
          <table className="projections-table">
            <thead>
              <tr>
                <th>Age</th>
                <th>Phase</th>
                <th>Total Balance</th>
                <th>Contribution</th>
                <th>Withdrawal</th>
                <th>Growth</th>
                <th>Tax</th>
                <th>Net Income</th>
                <th>SS Income</th>
                {profile.roth_conversion_enabled && <th>Roth Conv.</th>}
              </tr>
            </thead>
            <tbody>
              {milestones.map((snapshot, idx) => (
                <tr key={idx} className={`phase-${snapshot.phase}`}>
                  <td>{snapshot.age}</td>
                  <td className="phase-label">{snapshot.phase}</td>
                  <td>{formatCurrency(snapshot.total_balance)}</td>
                  <td>{formatCurrency(snapshot.total_contribution)}</td>
                  <td>{formatCurrency(snapshot.total_withdrawal)}</td>
                  <td>{formatCurrency(snapshot.total_growth)}</td>
                  <td>{formatCurrency(snapshot.tax_amount || 0)}</td>
                  <td>{formatCurrency(snapshot.net_withdrawal || 0)}</td>
                  <td>{formatCurrency(snapshot.ss_income || 0)}</td>
                  {profile.roth_conversion_enabled && <td>{formatCurrency(snapshot.roth_conversion || 0)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderComparisonTab = () => {
    if (!result || !result.traditional_comparisons || result.traditional_comparisons.length === 0) {
      return <p>No comparison data. Run a simulation first.</p>;
    }

    const comparison = result.traditional_comparisons.find(
      c => c.strategy_id === comparisonStrategy
    ) || result.traditional_comparisons[0];

    // Build chart data — merge bucket and traditional projections by age
    const bucketByAge: Record<number, number> = {};
    result.yearly_projections.forEach(p => { bucketByAge[p.age] = p.total_balance; });
    const tradByAge: Record<number, number> = {};
    comparison.yearly_projections.forEach(p => { tradByAge[p.age] = p.total_balance; });

    const allAges = Array.from(new Set([
      ...result.yearly_projections.map(p => p.age),
      ...comparison.yearly_projections.map(p => p.age)
    ])).sort((a, b) => a - b);

    const chartData = allAges.map(age => ({
      age,
      bucket: bucketByAge[age] ?? null,
      traditional: tradByAge[age] ?? null,
    }));

    // Phase boundaries for reference areas
    const phases = result.yearly_projections;
    const accumStart = phases.find(p => p.phase === 'accumulation')?.age;
    const accumEnd = [...phases].reverse().find(p => p.phase === 'accumulation')?.age;
    const transStart = phases.find(p => p.phase === 'transition')?.age;
    const transEnd = [...phases].reverse().find(p => p.phase === 'transition')?.age;
    const distStart = phases.find(p => p.phase === 'distribution')?.age;
    const distEnd = [...phases].reverse().find(p => p.phase === 'distribution')?.age;

    // KPI comparisons
    const bKpis = result.kpis;
    const tKpis = comparison.kpis;

    const yearsDiff = bKpis.years_of_income - tKpis.years_of_income;
    const legacyDiff = bKpis.legacy_amount - tKpis.legacy_amount;
    const retBalDiff = bKpis.projected_retirement_balance - tKpis.projected_retirement_balance;

    // Stress results
    const bucketFast = result.stress_results.find(s => s.name.toLowerCase().includes('fast'));
    const bucketSlow = result.stress_results.find(s => s.name.toLowerCase().includes('slow'));
    const tradFast = comparison.stress_results.find(s => s.name.toLowerCase().includes('fast'));
    const tradSlow = comparison.stress_results.find(s => s.name.toLowerCase().includes('slow'));

    // Growth allocation at age 80 — bucket system always has 50% in B4 longevity
    const tradAt80 = comparison.yearly_projections.find(p => p.age === 80);
    const tradStockAt80 = tradAt80 ? tradAt80.stock_pct : 0;

    const metricsRows: Array<{
      label: string;
      bucket: string;
      trad: string;
      advantage: string;
      positive: boolean | null;
    }> = [
      {
        label: 'Retirement Balance',
        bucket: formatCurrency(bKpis.projected_retirement_balance),
        trad: formatCurrency(tKpis.projected_retirement_balance),
        advantage: retBalDiff >= 0 ? `+${formatCurrency(retBalDiff)}` : formatCurrency(retBalDiff),
        positive: retBalDiff >= 0,
      },
      {
        label: 'Years of Income',
        bucket: `${bKpis.years_of_income}`,
        trad: `${tKpis.years_of_income}`,
        advantage: yearsDiff > 0 ? `+${yearsDiff} years` : yearsDiff === 0 ? 'Same' : `${yearsDiff} years`,
        positive: yearsDiff > 0 ? true : yearsDiff === 0 ? null : false,
      },
      {
        label: 'Legacy Amount',
        bucket: formatCurrency(bKpis.legacy_amount),
        trad: formatCurrency(tKpis.legacy_amount),
        advantage: legacyDiff >= 0 ? `+${formatCurrency(legacyDiff)}` : formatCurrency(legacyDiff),
        positive: legacyDiff >= 0,
      },
      {
        label: 'Annual Income',
        bucket: formatCurrency(bKpis.annual_retirement_income),
        trad: formatCurrency(tKpis.annual_retirement_income),
        advantage: '\u2014',
        positive: null,
      },
      {
        label: 'Survives Fast Crash',
        bucket: bucketFast ? (bucketFast.survived ? 'Yes' : 'No') : 'N/A',
        trad: tradFast ? (tradFast.survived ? 'Yes' : 'No') : 'N/A',
        advantage: bucketFast && tradFast
          ? (bucketFast.survived && !tradFast.survived ? 'Bucket wins' : bucketFast.survived === tradFast.survived ? 'Same' : 'Trad wins')
          : '\u2014',
        positive: bucketFast && tradFast ? (bucketFast.survived && !tradFast.survived ? true : bucketFast.survived === tradFast.survived ? null : false) : null,
      },
      {
        label: 'Survives Slow Crash',
        bucket: bucketSlow ? (bucketSlow.survived ? 'Yes' : 'No') : 'N/A',
        trad: tradSlow ? (tradSlow.survived ? 'Yes' : 'No') : 'N/A',
        advantage: bucketSlow && tradSlow
          ? (bucketSlow.survived && !tradSlow.survived ? 'Bucket wins' : bucketSlow.survived === tradSlow.survived ? 'Same' : 'Trad wins')
          : '\u2014',
        positive: bucketSlow && tradSlow ? (bucketSlow.survived && !tradSlow.survived ? true : bucketSlow.survived === tradSlow.survived ? null : false) : null,
      },
      {
        label: 'Growth Alloc at 80',
        bucket: '50%',
        trad: tradStockAt80 ? formatPercent(tradStockAt80) : 'N/A',
        advantage: tradStockAt80 ? `+${formatPercent(0.50 - tradStockAt80)}` : '\u2014',
        positive: tradStockAt80 ? 0.50 > tradStockAt80 : null,
      },
      {
        label: 'Effective Dist. Return',
        bucket: formatPercent(tKpis.effective_return > 0 ? tKpis.effective_return + 0.005 : 0.055),
        trad: formatPercent(tKpis.effective_return),
        advantage: '\u2014',
        positive: null,
      },
      {
        label: 'Portfolio Depleted',
        bucket: 'Never',
        trad: tKpis.portfolio_depleted_age ? `Age ${tKpis.portfolio_depleted_age}` : 'Never',
        advantage: tKpis.portfolio_depleted_age ? 'Bucket wins' : 'Same',
        positive: tKpis.portfolio_depleted_age ? true : null,
      },
    ];

    // Insight cards
    const insights: Array<{ value: string; label: string }> = [];
    if (yearsDiff > 0) {
      insights.push({ value: `+${yearsDiff} years`, label: `Your bucket system provides ${yearsDiff} more years of retirement income than ${comparison.strategy_name}` });
    }
    insights.push({ value: '5-7yr buffer', label: 'During a crash, your B1-B3 buffer protects growth assets from forced selling at depressed prices' });
    if (tKpis.portfolio_depleted_age) {
      insights.push({ value: `Age ${tKpis.portfolio_depleted_age}`, label: `${comparison.strategy_name} depletes at age ${tKpis.portfolio_depleted_age}, while your buckets leave ${formatCurrency(bKpis.legacy_amount)} legacy` });
    } else if (legacyDiff > 0) {
      insights.push({ value: formatCurrency(legacyDiff), label: `Your bucket system leaves ${formatCurrency(legacyDiff)} more in legacy than ${comparison.strategy_name}` });
    }

    return (
      <div className="lifecycle-comparison">
        {/* 1. Strategy Toggle */}
        <div className="comparison-strategy-toggle">
          <button
            className={`comparison-pill ${comparisonStrategy === 'sixty_forty' ? 'active' : ''}`}
            onClick={() => setComparisonStrategy('sixty_forty')}
          >
            60/40 + 4% Rule
          </button>
          <button
            className={`comparison-pill ${comparisonStrategy === 'age_in_bonds' ? 'active' : ''}`}
            onClick={() => setComparisonStrategy('age_in_bonds')}
          >
            Age-in-Bonds
          </button>
        </div>

        {/* 2. Portfolio Trajectory Chart */}
        <div className="comparison-chart-container">
          <h4>Portfolio Trajectory: 4-Bucket vs {comparison.strategy_name}</h4>
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
              {accumStart != null && accumEnd != null && (
                <ReferenceArea x1={accumStart} x2={accumEnd} fill="#3b82f6" fillOpacity={0.06} />
              )}
              {transStart != null && transEnd != null && (
                <ReferenceArea x1={transStart} x2={transEnd} fill="#f59e0b" fillOpacity={0.06} />
              )}
              {distStart != null && distEnd != null && (
                <ReferenceArea x1={distStart} x2={distEnd} fill="#22c55e" fillOpacity={0.06} />
              )}
              <XAxis dataKey="age" stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 12 }} />
              <YAxis stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 12 }} tickFormatter={formatCompactCurrency} />
              <Tooltip
                contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, color: '#e2e8f0' }}
                labelFormatter={(age) => `Age ${age}`}
                formatter={(value: any, name?: string) => {
                  const label = name === 'bucket' ? '4-Bucket System' : comparison.strategy_name;
                  return [formatCurrency(value ?? 0), label];
                }}
              />
              <Legend
                formatter={(value: string) => value === 'bucket' ? '4-Bucket System' : comparison.strategy_name}
                wrapperStyle={{ color: '#94a3b8' }}
              />
              <Line type="monotone" dataKey="bucket" stroke="#3b82f6" strokeWidth={2.5} dot={false} connectNulls />
              <Line type="monotone" dataKey="traditional" stroke="#ef4444" strokeWidth={2} strokeDasharray="6 3" dot={false} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* 3. Comparison Metrics Table */}
        <h4 className="comparison-section-heading">Comparison Metrics</h4>
        <div className="comparison-table-container">
          <table className="comparison-table">
            <thead>
              <tr>
                <th>Metric</th>
                <th>4-Bucket System</th>
                <th>{comparison.strategy_name}</th>
                <th>Advantage</th>
              </tr>
            </thead>
            <tbody>
              {metricsRows.map((row, idx) => (
                <tr key={idx}>
                  <td>{row.label}</td>
                  <td>{row.bucket}</td>
                  <td>{row.trad}</td>
                  <td className={row.positive === true ? 'advantage-positive' : row.positive === false ? 'advantage-negative' : 'advantage-neutral'}>
                    {row.advantage}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 4. Crash Impact Cards */}
        {(bucketFast || bucketSlow) && (
          <div className="crash-comparison-section">
            <h4 className="comparison-section-heading">Crash Impact Comparison</h4>

            {bucketFast && tradFast && (
              <>
                <div className="crash-scenario-label">Fast Recovery (COVID-like)</div>
                <div className="crash-comparison-grid">
                  <div className="crash-card bucket-system">
                    <div className="crash-card-title">
                      <span>4-Bucket System</span>
                      <span className={`crash-badge ${bucketFast.survived ? 'survived' : 'depleted'}`}>
                        {bucketFast.survived ? 'SURVIVED' : 'DEPLETED'}
                      </span>
                    </div>
                    <div className="crash-card-stats">
                      <span>Pre-stress: {formatCurrency(bucketFast.pre_stress_balance)}</span>
                      <span>Post-stress: {formatCurrency(bucketFast.post_stress_balance)}</span>
                      <span>Recovery: {formatCurrency(bucketFast.recovery_balance)}</span>
                    </div>
                    <div className="crash-card-note">B1-B3 buffer withdrawals protect growth assets</div>
                  </div>
                  <div className="crash-card traditional">
                    <div className="crash-card-title">
                      <span>{comparison.strategy_name}</span>
                      <span className={`crash-badge ${tradFast.survived ? 'survived' : 'depleted'}`}>
                        {tradFast.survived ? 'SURVIVED' : 'DEPLETED'}
                      </span>
                    </div>
                    <div className="crash-card-stats">
                      <span>Pre-stress: {formatCurrency(tradFast.pre_stress_balance)}</span>
                      <span>Post-stress: {formatCurrency(tradFast.post_stress_balance)}</span>
                      <span>Recovery: {formatCurrency(tradFast.recovery_balance)}</span>
                    </div>
                    <div className="crash-card-note">Forced selling at depressed prices</div>
                  </div>
                </div>
              </>
            )}

            {bucketSlow && tradSlow && (
              <>
                <div className="crash-scenario-label">Slow Recovery (2008-like)</div>
                <div className="crash-comparison-grid">
                  <div className="crash-card bucket-system">
                    <div className="crash-card-title">
                      <span>4-Bucket System</span>
                      <span className={`crash-badge ${bucketSlow.survived ? 'survived' : 'depleted'}`}>
                        {bucketSlow.survived ? 'SURVIVED' : 'DEPLETED'}
                      </span>
                    </div>
                    <div className="crash-card-stats">
                      <span>Pre-stress: {formatCurrency(bucketSlow.pre_stress_balance)}</span>
                      <span>Post-stress: {formatCurrency(bucketSlow.post_stress_balance)}</span>
                      <span>Recovery: {formatCurrency(bucketSlow.recovery_balance)}</span>
                    </div>
                    <div className="crash-card-note">B1-B3 buffer withdrawals protect growth assets</div>
                  </div>
                  <div className="crash-card traditional">
                    <div className="crash-card-title">
                      <span>{comparison.strategy_name}</span>
                      <span className={`crash-badge ${tradSlow.survived ? 'survived' : 'depleted'}`}>
                        {tradSlow.survived ? 'SURVIVED' : 'DEPLETED'}
                      </span>
                    </div>
                    <div className="crash-card-stats">
                      <span>Pre-stress: {formatCurrency(tradSlow.pre_stress_balance)}</span>
                      <span>Post-stress: {formatCurrency(tradSlow.post_stress_balance)}</span>
                      <span>Recovery: {formatCurrency(tradSlow.recovery_balance)}</span>
                    </div>
                    <div className="crash-card-note">Forced selling at depressed prices</div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* 5. Bucket Cascade Diagram */}
        <div className="cascade-section">
          <h4>How the 4-Bucket System Protects You</h4>

          <div className="cascade-scenario-label">Normal Year</div>
          <div className="cascade-row">
            <div className="cascade-box expenses">Expenses</div>
            <span className="cascade-arrow">&larr;</span>
            <div className="cascade-box">B1 Cash 2%</div>
            <span className="cascade-arrow">&larr; refill</span>
            <div className="cascade-box">B2 Stability 3.5%</div>
            <span className="cascade-arrow">&larr; refill</span>
            <div className="cascade-box">B3 Growth 5.5%</div>
            <div className="cascade-box protected">B4 Longevity 7% grows</div>
          </div>

          <div className="cascade-scenario-label">Crash Year</div>
          <div className="cascade-row">
            <div className="cascade-box expenses">Expenses</div>
            <span className="cascade-arrow">&larr;</span>
            <div className="cascade-box">B1 Cash</div>
            <span className="cascade-arrow">&larr;</span>
            <div className="cascade-box">B2 Buffer</div>
            <span className="cascade-arrow">&larr;</span>
            <div className="cascade-box">B3 Bridge: 5-7yr runway</div>
            <div className="cascade-box protected">B4 Longevity: recovers untouched</div>
          </div>

          <div className="cascade-scenario-label">Traditional Crash</div>
          <div className="cascade-row traditional">
            <div className="cascade-box expenses">Expenses</div>
            <span className="cascade-arrow">&larr; sell from</span>
            <div className="cascade-box danger">Entire Portfolio at -30%</div>
            <span className="cascade-arrow">&rarr;</span>
            <div className="cascade-box danger">Slower recovery &rarr; Depletes faster</div>
          </div>
        </div>

        {/* 6. Insight Cards */}
        {insights.length > 0 && (
          <div className="insight-cards">
            {insights.map((insight, idx) => (
              <div key={idx} className="insight-card">
                <div className="insight-value">{insight.value}</div>
                <div className="insight-label">{insight.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderChatTab = () => {
    if (!result) return <p>Run a simulation first to chat about your results.</p>;

    if (!isPremier) {
      return (
        <div className="lifecycle-chat">
          <div className="chat-upgrade">
            <span className="chat-upgrade-icon">&#x2728;</span>
            <div>
              <strong>AI-Powered Chat</strong>
              <p>Upgrade to Premier to ask AI follow-up questions about your simulation results — get personalized insights on your retirement projections, bucket allocations, and stress scenarios.</p>
            </div>
            <a href="/upgrade" className="btn-primary" style={{ whiteSpace: 'nowrap', textDecoration: 'none', padding: '0.5rem 1.5rem', borderRadius: '8px', fontWeight: 600 }}>Upgrade</a>
          </div>
        </div>
      );
    }

    return (
      <div className="lifecycle-chat">
        <h3>Ask About Your Plan</h3>
        <p className="chat-hint">Ask questions about your retirement projections. The AI has access to your simulation data, KPIs, stress results, and conversation history.</p>

        <div className="chat-history">
          {chatHistory.map((entry, idx) => (
            <div key={idx} className="chat-entry">
              <div className="chat-question">
                <strong>You:</strong> {entry.question}
              </div>
              <div className="chat-answer">
                <strong>AI:</strong>
                <div
                  className="chat-answer-body"
                  dangerouslySetInnerHTML={{ __html: formatMarkdown(entry.answer) }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="chat-input-container">
          <input
            type="text"
            value={chatQuestion}
            onChange={e => setChatQuestion(e.target.value)}
            placeholder="Ask a question about your retirement plan..."
            onKeyPress={e => e.key === 'Enter' && sendChatMessage()}
            disabled={chatLoading}
          />
          <button
            onClick={sendChatMessage}
            disabled={chatLoading || !chatQuestion.trim()}
            className="btn-send"
          >
            {chatLoading ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    );
  };

  // Example scenarios for the guide modal
  const exampleScenarios = [
    {
      title: 'Early Career Starter',
      description: 'A 25-year-old just beginning their career, investing aggressively with a long runway to retirement.',
      stats: { age: 25, income: '$75K', savings: '$20K', retire: 65 },
      profile: { current_age: 25, retirement_age: 65, life_expectancy: 95, current_savings: 20000, annual_income: 75000, annual_expenses: 45000, emergency_fund_months: 6, filing_status: 'single', ss_monthly_benefit: 1800, ss_start_age: 67 } as UserProfile,
      allocation: { savings: 0.05, growth: 0.35, dividend: 0.10, super_long_term: 0.50 } as AllocationWeights,
      annualContribution: 12000,
      stressMode: 'both' as const,
      stressDrawdownPct: 0.30,
      stressTiming: 'pre_retirement' as const,
    },
    {
      title: 'Mid-Career Professional',
      description: 'A 40-year-old with solid savings and income, maintaining a balanced portfolio for steady growth.',
      stats: { age: 40, income: '$150K', savings: '$250K', retire: 65 },
      profile: { current_age: 40, retirement_age: 65, life_expectancy: 95, current_savings: 250000, annual_income: 150000, annual_expenses: 85000, emergency_fund_months: 6, filing_status: 'married_joint', ss_monthly_benefit: 2500, ss_start_age: 67 } as UserProfile,
      allocation: { savings: 0.10, growth: 0.40, dividend: 0.25, super_long_term: 0.25 } as AllocationWeights,
      annualContribution: 30000,
      stressMode: 'both' as const,
      stressDrawdownPct: 0.30,
      stressTiming: 'pre_retirement' as const,
    },
    {
      title: 'Late Career Catch-Up',
      description: 'A 52-year-old with moderate savings who needs a conservative, income-focused strategy to close the gap.',
      stats: { age: 52, income: '$120K', savings: '$150K', retire: 67 },
      profile: { current_age: 52, retirement_age: 67, life_expectancy: 90, current_savings: 150000, annual_income: 120000, annual_expenses: 70000, emergency_fund_months: 6, filing_status: 'single', ss_monthly_benefit: 2200, ss_start_age: 67 } as UserProfile,
      allocation: { savings: 0.15, growth: 0.30, dividend: 0.35, super_long_term: 0.20 } as AllocationWeights,
      annualContribution: 25000,
      stressMode: 'both' as const,
      stressDrawdownPct: 0.25,
      stressTiming: 'early_retirement' as const,
    },
    {
      title: 'Early Retirement Seeker',
      description: 'A high-earning 35-year-old aiming to retire at 55 with aggressive savings, growth-heavy allocation, and Roth conversion during the 12-year Goldilocks window.',
      stats: { age: 35, income: '$250K', savings: '$500K', retire: 55 },
      profile: { current_age: 35, retirement_age: 55, life_expectancy: 95, current_savings: 500000, annual_income: 250000, annual_expenses: 100000, emergency_fund_months: 6, filing_status: 'married_joint', ss_monthly_benefit: 3000, ss_start_age: 67, roth_conversion_enabled: true, pre_tax_percentage: 0.80, roth_target_bracket: 0.22, rmd_start_age: 73 } as UserProfile,
      allocation: { savings: 0.05, growth: 0.45, dividend: 0.15, super_long_term: 0.35 } as AllocationWeights,
      annualContribution: 60000,
      stressMode: 'both' as const,
      stressDrawdownPct: 0.35,
      stressTiming: 'pre_retirement' as const,
    },
  ];

  const loadScenario = (scenario: typeof exampleScenarios[0]) => {
    setProfile(scenario.profile);
    setAllocation(scenario.allocation);
    setAnnualContribution(scenario.annualContribution);
    setStressMode(scenario.stressMode);
    setStressDrawdownPct(scenario.stressDrawdownPct);
    setStressTiming(scenario.stressTiming);
    setShowGuide(false);
    setActiveTab('input');
  };

  const renderGuideModal = () => {
    if (!showGuide) return null;

    return (
      <div className="guide-overlay" onClick={() => setShowGuide(false)}>
        <div className="guide-modal" onClick={e => e.stopPropagation()}>
          <button className="guide-close" onClick={() => setShowGuide(false)}>&times;</button>

          <div className="guide-section">
            <h3>What is the Lifecycle Planner?</h3>
            <p>
              The Lifecycle Investment Planner is a deterministic retirement simulator that models your financial
              journey across three phases: <strong>accumulation</strong> (saving and investing while working),
              <strong> transition</strong> (gradually shifting to conservative assets near retirement), and
              <strong> distribution</strong> (drawing down your portfolio in retirement). Your contributions
              are split across four investment buckets, each with different risk and return profiles, and
              you can stress-test your plan against market crashes to see if your portfolio survives.
            </p>
            <p>
              Retirement withdrawals are based on your <strong>actual annual expenses</strong>, so the simulator
              models what you really need to live on. When Social Security hasn't started yet, the full amount comes
              from your portfolio; once SS kicks in, it offsets part of the draw. This lets you see how the
              gap between retirement and SS impacts your portfolio.
            </p>
          </div>

          <div className="guide-section">
            <h3>How to Use It</h3>
            <div className="guide-steps">
              <div className="guide-step">
                <strong>Your Profile</strong> &mdash; Enter your current age, target retirement age,
                life expectancy, current savings, annual net income, annual expenses, annual contribution,
                and expected inflation rate. Your annual expenses drive retirement withdrawals &mdash; the
                simulator will draw this amount (inflation-adjusted) each year from your portfolio.
              </div>
              <div className="guide-step">
                <strong>Social Security & Taxes</strong> &mdash; Enter your expected monthly Social Security
                benefit and the age you plan to start collecting. Choose your filing status for tax bracket
                calculations, or enter a custom flat tax rate to override federal brackets.
              </div>
              <div className="guide-step">
                <strong>Roth Conversion Strategy</strong> &mdash; Enable this to model converting pre-tax
                money to Roth during the <em>Goldilocks window</em> &mdash; the low-income period between
                retirement and when SS/RMDs begin. Set your pre-tax portfolio percentage, target tax bracket
                to fill up to, and RMD start age. The simulator converts as much as fits under the bracket
                ceiling each year, paying the conversion tax from your portfolio. This reduces future
                taxable withdrawals once SS and RMDs kick in.
              </div>
              <div className="guide-step">
                <strong>Bucket Allocation</strong> &mdash; Divide your contributions across four buckets.
                <em> Savings</em> (~1% return, cash and emergency reserves),
                <em> Growth</em> (~7%, stocks and equity funds),
                <em> Dividend</em> (~4%, income-producing assets), and
                <em> Super Long Term</em> (~9%, aggressive growth). Allocations must total 100%.
              </div>
              <div className="guide-step">
                <strong>Stress Testing</strong> &mdash; Simulate a market crash by choosing the base drawdown
                percentage and when the crash hits. The simulator runs two scenarios: <em>Fast Recovery</em> uses
                your chosen drawdown with a 1-year V-shaped recovery (COVID-like), while <em>Slow Recovery</em> uses
                a 1.5&times; deeper drawdown with a 5-year grind back (2008-like).
              </div>
              <div className="guide-step">
                <strong>Results Tab</strong> &mdash; After running the simulation, view KPI cards
                (retirement balance, income replacement ratio, legacy amount, Roth conversion totals)
                and stress test survival status.
              </div>
              <div className="guide-step">
                <strong>Projections Tab</strong> &mdash; A year-by-year table showing your balance,
                contributions, withdrawals, growth, taxes, net income, SS income, and Roth conversions at each age.
              </div>
              <div className="guide-step">
                <strong>AI Chat Tab</strong> &mdash; Ask follow-up questions about your simulation
                results and get personalized insights.
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h3>Example Scenarios</h3>
            <p className="guide-scenarios-hint">Click "Load This Example" to pre-fill the form with a ready-to-run scenario.</p>
            <div className="guide-scenarios">
              {exampleScenarios.map((scenario, idx) => (
                <div key={idx} className="scenario-card">
                  <h4>{scenario.title}</h4>
                  <p>{scenario.description}</p>
                  <div className="scenario-stats">
                    <span>Age: {scenario.stats.age}</span>
                    <span>Income: {scenario.stats.income}</span>
                    <span>Savings: {scenario.stats.savings}</span>
                    <span>Retire: {scenario.stats.retire}</span>
                  </div>
                  <button className="btn-load-scenario" onClick={() => loadScenario(scenario)}>
                    Load This Example
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="lifecycle-container">
      <Navigation />

      <main className="lifecycle-main">
        <div className="lifecycle-header">
          <h2>Lifecycle Investment Planner</h2>
          <p>
            Plan your path from accumulation through retirement with our deterministic lifecycle model.
            {' '}<button className="guide-link" onClick={() => setShowGuide(true)}>How It Works</button>
          </p>
        </div>

        <div className="tool-purpose-banner">
          <span className="tpb-icon">🗂️</span>
          <span>
            <strong>Use this tool if</strong> you want a complete financial life model — income, expenses, taxes, Roth conversions, Social Security, and withdrawal planning modeled year by year from now through retirement.{' '}
            Just want to know when you'll hit your savings number?{' '}
            Use <strong>FIRE Strategy</strong> instead.
          </span>
        </div>

        {renderGuideModal()}

        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError(null)}>Dismiss</button>
          </div>
        )}

        <div className="lifecycle-tabs">
          <button
            className={`tab-btn ${activeTab === 'input' ? 'active' : ''}`}
            onClick={() => setActiveTab('input')}
          >
            Input
          </button>
          <button
            className={`tab-btn ${activeTab === 'results' ? 'active' : ''}`}
            onClick={() => setActiveTab('results')}
            disabled={!result}
          >
            Results
          </button>
          <button
            className={`tab-btn ${activeTab === 'comparison' ? 'active' : ''}`}
            onClick={() => setActiveTab('comparison')}
            disabled={!result?.traditional_comparisons?.length}
          >
            Comparison
          </button>
          <button
            className={`tab-btn ${activeTab === 'projections' ? 'active' : ''}`}
            onClick={() => setActiveTab('projections')}
            disabled={!result}
          >
            Projections
          </button>
          <button
            className={`tab-btn ${activeTab === 'chat' ? 'active' : ''}`}
            onClick={() => setActiveTab('chat')}
            disabled={!result}
          >
            AI Chat
          </button>
        </div>

        <div className="lifecycle-content">
          {activeTab === 'input' && renderInputTab()}
          {activeTab === 'results' && renderResultsTab()}
          {activeTab === 'comparison' && renderComparisonTab()}
          {activeTab === 'projections' && renderProjectionsTab()}
          {activeTab === 'chat' && renderChatTab()}
        </div>
      </main>
    </div>
  );
};
